import { bufferDefaultsOf, leadTimeOf, offerPolicyOf } from '@yourstore/domain';
import type { OfferMode, TenantConfig, TenantOfferPolicy } from '@yourstore/domain';
import type { T } from '../../../messages';

/**
 * **The booking rule, as a sentence** — "Love Spa works in 30-minute steps,
 * leaves 30 minutes between clients, and offers any open time. No minimum
 * notice." (console redesign B2).
 *
 * Everything the rule is made of is in `tenant_config.config`, resolved
 * through `@yourstore/domain`'s own readers — the ones the engine reads — so
 * the sentence says what will actually be offered, not what was typed:
 *
 *  * the step (`slot_grid_min`, 15 when nobody set one — the schema's default);
 *  * the gap after and before (`default_buffer_*`);
 *  * the minimum notice and the same-day cut-off;
 *  * the offer policy's mode, the hours kept back, and whether the rest of it
 *    (rank, anchors, how many are read out first, the useful gap) has been
 *    tuned — named, not spelled out, because the simulator is where those are
 *    understood.
 *
 * The highlighted parts are `**…**`, which `Rich` renders; `setup.css` draws
 * them as the mockup's highlight.
 */
export interface Rule {
  step: number;
  before: number;
  after: number;
  lead: number;
  cutoff: string | null;
  policy: TenantOfferPolicy | null;
}

export function ruleOf(config: Partial<TenantConfig> | null): Rule {
  const buffers = bufferDefaultsOf(config);
  const lead = leadTimeOf(config);
  return {
    step: typeof config?.slot_grid_min === 'number' ? config.slot_grid_min : 15,
    before: buffers.before_min,
    after: buffers.after_min,
    lead: lead.min_lead_minutes,
    cutoff: lead.same_day_cutoff,
    policy: offerPolicyOf(config),
  };
}

/** "30 minutes", "2 hours", "1 day", "1 h 30 min". */
export function duration(t: T, minutes: number): string {
  if (minutes >= 1440 && minutes % 1440 === 0) return t('setup.rule.days', { n: minutes / 1440 });
  if (minutes >= 60 && minutes % 60 === 0) return t('setup.rule.hours', { n: minutes / 60 });
  if (minutes > 60) {
    return t('setup.rule.hoursMinutes', { h: Math.floor(minutes / 60), m: minutes % 60 });
  }
  return t('setup.rule.minutes', { n: minutes });
}

export function modePhrase(t: T, mode: OfferMode): string {
  switch (mode) {
    case 'open':
      return t('setup.rule.mode.open');
    case 'no_slivers':
      return t('setup.rule.mode.no_slivers');
    case 'touching':
      return t('setup.rule.mode.touching');
  }
}

export function ruleSentence(t: T, name: string, rule: Rule): string {
  const policy = rule.policy;
  const mode = modePhrase(t, policy?.mode ?? 'open');
  const step = t('setup.rule.step', { n: rule.step });
  const kept = policy?.protected?.length ?? 0;
  const tuned =
    (policy?.anchors?.length ?? 0) > 0 ||
    policy?.top_n !== undefined ||
    policy?.min_useful_gap_min !== undefined;
  return [
    rule.after > 0
      ? t('setup.rule.sentence', { name, step, gap: duration(t, rule.after), mode })
      : t('setup.rule.sentenceNoGap', { name, step, mode }),
    rule.before > 0 ? t('setup.rule.before', { gap: duration(t, rule.before) }) : '',
    rule.lead > 0
      ? t('setup.rule.notice', { notice: duration(t, rule.lead) })
      : t('setup.rule.noNotice'),
    rule.cutoff === null ? '' : t('setup.rule.cutoff', { time: rule.cutoff }),
    kept > 0 ? t('setup.rule.keptBack', { count: kept }) : '',
    policy?.rank === 'packing' ? t('setup.rule.packing') : '',
    tuned ? t('setup.rule.fineTuned') : '',
  ]
    .filter((s) => s !== '')
    .join(' ');
}
